import { useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/all';
import { useGSAP } from '@gsap/react';
import { animateWithGsap } from '../utils/animations';
import { ADDONS, BASE_PAGE_NAMES, PRICING, STEPS, OFFER } from '../constants';
import { fmt, offerActive } from '../utils';
import { Laptop, Phone, Screen, Shot } from './Devices';

gsap.registerPlugin(ScrollTrigger);

const BUY = import.meta.env.BASE_URL;
const A = `${import.meta.env.BASE_URL}assets/rak`;
const SHOTS = { coach: `${A}/case/coachtoreach.webp`, greentist: `${A}/case/image.webp`, omc: `${A}/case/omc.webp` };
const MOBILE = { coach: `${A}/case/coach2reach-mobile.webp`, aesthetics: `${A}/case/modernaestheticss-mobile.webp`, coppre: `${A}/case/studiocoppre-mobile.webp`, omc: `${A}/case/omc-mobile.webp` };
const LOGOS = ['bridgesspeechcenter','modernaestheticss','coach2reach','omcmedical','studiocoppre','tlcskyn','technoaircons','saneventhall','calciumclinics','asiantrader','kidulan','greentist'];
const price = PRICING.platforms.static.price;

/* ---------- hero ---------- */
const Hero = () => {
  useGSAP(() => {
    gsap.to('.h-in', { opacity: 1, y: 0, duration: 1, stagger: 0.12, ease: 'power3.out', delay: 0.15 });
    gsap.fromTo('#hero-laptop', { y: 60, opacity: 0, scale: 0.96 }, { y: 0, opacity: 1, scale: 1, duration: 1.3, ease: 'power3.out', delay: 0.5 });
    gsap.fromTo('#hero-phone', { y: 90, opacity: 0 }, { y: 0, opacity: 1, duration: 1.3, ease: 'power3.out', delay: 0.8 });
    gsap.to('#hero-laptop', { y: -40, ease: 'none', scrollTrigger: { trigger: '#hero', start: 'top top', end: 'bottom top', scrub: true } });
    gsap.to('#hero-phone', { y: -90, ease: 'none', scrollTrigger: { trigger: '#hero', start: 'top top', end: 'bottom top', scrub: true } });
  }, []);

  return (
    <section id="hero" className="overflow-hidden bg-[#F1F5FB] px-5 pb-24 pt-14 text-center sm:px-10 sm:pt-20">
      <div className="screen-max-width">
        <p className="h-in translate-y-6 text-sm font-semibold text-gray opacity-0">Website in 24 Hours</p>
        <h1 className="h-in mt-3 translate-y-6 text-5xl font-semibold tracking-tight text-ink opacity-0 sm:text-7xl md:text-[5.5rem] md:leading-[1]">
          Your business,<br />online by tomorrow.
        </h1>
        <p className="h-in mx-auto mt-6 max-w-xl translate-y-6 text-lg text-gray opacity-0 sm:text-xl">
          A complete 7-page website with logo, content, domain and hosting. Built for you in 24 hours.
        </p>
        <div className="h-in mt-8 flex translate-y-6 flex-col items-center gap-3 opacity-0">
          <a href={BUY} className="btn btn--primary !px-8 !py-3 !text-base">Buy</a>
          <span className="tabular text-sm text-gray">From ₹{fmt(price)}</span>
        </div>

        <div className="relative mx-auto mt-16 max-w-4xl">
          <div id="hero-laptop">
            <Laptop className="mx-auto w-full"><Shot src={SHOTS.omc} alt="OMC Medical website" /></Laptop>
          </div>
          <div id="hero-phone" className="absolute -bottom-6 right-2 w-28 sm:-right-6 sm:w-40 md:w-44">
            <Phone><Shot src={MOBILE.omc} alt="OMC Medical on a phone" /></Phone>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- trusted logos ---------- */
const Trusted = () => (
  <section className="bg-white px-5 py-14 sm:px-10">
    <div className="screen-max-width">
      <p className="text-center text-xs font-semibold uppercase tracking-[0.18em] text-gray">Built for 200+ businesses</p>
      <div className="mt-8 grid grid-cols-3 items-center gap-x-8 gap-y-6 sm:grid-cols-4 lg:grid-cols-6">
        {LOGOS.map((l) => (
          <img key={l} src={`${A}/logos/${l}.webp`} alt={l} className="mx-auto h-8 w-auto max-w-[120px] object-contain opacity-60 grayscale transition hover:opacity-100 hover:grayscale-0" loading="lazy" />
        ))}
      </div>
    </div>
  </section>
);

/* ---------- pinned "7 pages" ---------- */
const Pages = () => {
  const wrap = useRef();
  useGSAP(() => {
    const cards = gsap.utils.toArray('.pg-card');
    gsap.set(cards, { yPercent: 120, rotate: (i) => (i % 2 ? 6 : -6), opacity: 0 });
    const tl = gsap.timeline({
      scrollTrigger: { trigger: wrap.current, start: 'top top', end: `+=${cards.length * 260}`, scrub: 0.6, pin: true },
    });
    cards.forEach((c, i) => {
      tl.to(c, { yPercent: 0, y: -i * 6, x: i * 4, rotate: (i - 3) * 1.5, opacity: 1, duration: 1, ease: 'power2.out' }, i * 0.8);
      tl.to('#pg-count', { textContent: i + 1, snap: { textContent: 1 }, duration: 0.2 }, i * 0.8);
    });
  }, []);

  return (
    <section ref={wrap} className="relative flex h-screen items-center overflow-hidden bg-ink text-white">
      <div className="screen-max-width w-full px-5 sm:px-10">
        <div className="grid items-center gap-10 md:grid-cols-2">
          <div>
            <p className="text-sm font-semibold text-blue">Pages</p>
            <h2 className="mt-3 text-5xl font-semibold tracking-tight sm:text-7xl">
              <span id="pg-count" className="tabular">0</span> pages.
            </h2>
            <p className="mt-4 max-w-md text-lg text-white/60">
              Home, About, Services, Gallery, Testimonials, FAQ and Contact. All written for you from your inputs. Need more? Add pages for ₹{fmt(PRICING.perPage)} each.
            </p>
          </div>
          <div className="relative mx-auto h-[420px] w-full max-w-[340px]">
            {BASE_PAGE_NAMES.map((p, i) => (
              <div
                key={p}
                className="pg-card absolute inset-0 overflow-hidden rounded-2xl border border-white/10 bg-white shadow-[0_30px_60px_-20px_rgba(0,0,0,0.6)]"
                style={{ zIndex: i }}
              >
                <div className="h-full">
                  <Shot src={[SHOTS.omc, SHOTS.coach, SHOTS.greentist][i % 3]} alt={p} />
                </div>
                <span className="absolute bottom-3 left-3 rounded-full bg-ink px-2.5 py-1 text-[10px] font-semibold text-white">{p}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- feature chapters ---------- */
const Chapter = ({ eyebrow, title, text, media, flip = false, alt = false }) => (
  <section className={`px-5 py-24 sm:px-10 sm:py-32 ${alt ? 'bg-[#F1F5FB]' : 'bg-white'}`}>
    <div className={`screen-max-width grid items-center gap-12 md:grid-cols-2 ${flip ? 'md:[&>*:first-child]:order-2' : ''}`}>
      <div className="ch g_fadeIn">
        <p className="text-sm font-semibold text-blue">{eyebrow}</p>
        <h2 className="mt-3 text-4xl font-semibold tracking-tight text-ink sm:text-6xl">{title}</h2>
        <p className="mt-5 max-w-md text-lg text-gray">{text}</p>
      </div>
      <div className="ch g_fadeIn">{media}</div>
    </div>
  </section>
);

const LogoMedia = () => (
  <div className="grid grid-cols-3 gap-4">
    {['bridgesspeechcenter', 'tlcskyn', 'studiocoppre', 'modernaestheticss', 'saneventhall', 'kidulan'].map((l, i) => (
      <div key={l} className="flex aspect-square items-center justify-center rounded-3xl bg-white p-5 shadow-[0_12px_30px_-16px_rgba(0,0,0,0.25)]" style={{ transform: `rotate(${((i % 3) - 1) * 2}deg)` }}>
        <img src={`${A}/logos/${l}.webp`} alt={l} className="max-h-14 w-auto max-w-full object-contain" loading="lazy" />
      </div>
    ))}
  </div>
);

const DomainMedia = () => (
  <div className="rounded-3xl bg-ink p-8 text-white shadow-[0_30px_60px_-25px_rgba(0,0,0,0.5)]">
    <p className="text-xs text-white/50">https://</p>
    <p className="text-3xl font-semibold tracking-tight sm:text-4xl">yourbusiness.com</p>
    <div className="mt-6 grid grid-cols-3 gap-3 text-[11px]">
      {[['Domain', '1 year'], ['Hosting', '1 year'], ['SSL', 'Included']].map(([k, v]) => (
        <div key={k} className="rounded-2xl bg-white/10 p-3">
          <p className="text-white/50">{k}</p>
          <p className="mt-1 font-semibold">{v}</p>
        </div>
      ))}
    </div>
  </div>
);

const PhonesMedia = () => (
  <div className="flex items-end justify-center gap-4">
    <Phone className="w-32 sm:w-40"><Shot src={MOBILE.aesthetics} alt="Modern Aestheticss on a phone" /></Phone>
    <Phone className="w-32 sm:w-40 sm:-translate-y-6"><Shot src={MOBILE.coppre} alt="Studio Coppre on a phone" /></Phone>
  </div>
);

/* ---------- compare ---------- */
const Compare = () => {
  const isOfferActive = offerActive();
  
  return (
    <section className="bg-white px-5 py-24 sm:px-10 sm:py-32">
      <div className="screen-max-width">
        <h2 className="g_fadeIn text-center text-4xl font-semibold tracking-tight text-ink sm:text-6xl">Which one is right for you?</h2>
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {[
            { name: 'Standard', p: price, tag: 'Fast and secure', pts: ['Hand-built, no admin panel', 'We make changes for you', 'Fastest load times', 'Everything included'] },
            { name: 'WordPress', p: PRICING.platforms.wp.price, tag: 'Edit it yourself', pts: ['Full admin dashboard', 'Add pages and posts anytime', 'Install plugins', 'Everything included'] },
          ].map((c) => (
            <div key={c.name} className="g_fadeIn rounded-3xl border border-[#E2E8F0] bg-[#F1F5FB] p-8">
              <p className="text-sm font-semibold text-blue">{c.tag}</p>
              <h3 className="mt-2 text-3xl font-semibold text-ink">{c.name}</h3>
              {c.name === 'WordPress' && isOfferActive ? (
                <>
                  <p className="tabular mt-1 text-gray">
                    <span className="mr-2 text-xs text-gray line-through">₹{fmt(PRICING.platforms.wp.price)}</span>
                    ₹{fmt(price)}
                  </p>
                  <p className="mt-1 text-xs font-medium text-[#1d9c5e]">Free WordPress upgrade, limited-time offer</p>
                </>
              ) : (
                <p className="tabular mt-1 text-gray">₹{fmt(c.p)}</p>
              )}
              <ul className="mt-6 space-y-2 text-sm text-ink">
                {c.pts.map((t) => (
                  <li key={t} className="flex gap-2"><span className="text-blue">✓</span>{t}</li>
                ))}
              </ul>
              <a href={BUY} className="btn btn--primary mt-8 w-full">Buy</a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ---------- add-ons teaser ---------- */
const Extras = () => (
  <section className="bg-[#F1F5FB] px-5 py-24 sm:px-10 sm:py-32">
    <div className="screen-max-width">
      <p className="g_fadeIn text-center text-sm font-semibold text-blue">Add-ons</p>
      <h2 className="g_fadeIn mt-3 text-center text-4xl font-semibold tracking-tight text-ink sm:text-6xl">Make it yours.</h2>
      <div className="mt-12 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {ADDONS.map((a) => (
          <div key={a.id} className="g_fadeIn rounded-3xl bg-white p-6">
            <p className="text-lg font-semibold text-ink">{a.title}</p>
            <p className="mt-1 text-sm text-gray">{a.desc}</p>
            <p className="tabular mt-4 text-sm font-semibold text-ink">+ ₹{fmt(a.price)}</p>
          </div>
        ))}
        <a href={BUY} className="g_fadeIn flex flex-col justify-between rounded-3xl bg-ink p-6 text-white">
          <p className="text-lg font-semibold">Pick what you need.</p>
          <p className="mt-6 text-sm text-white/70">The price adds up as you go →</p>
        </a>
      </div>
    </div>
  </section>
);

/* ---------- steps ---------- */
const Steps = () => (
  <section className="bg-white px-5 py-24 sm:px-10 sm:py-32">
    <div className="screen-max-width">
      <h2 className="g_fadeIn text-center text-4xl font-semibold tracking-tight text-ink sm:text-6xl">From inputs to live. In a day.</h2>
      <div className="mt-14 grid gap-10 md:grid-cols-3">
        {STEPS.map((s, i) => (
          <div key={s.hour} className="g_fadeIn">
            <p className="text-6xl font-semibold text-[#E2E8F0]">{i + 1}</p>
            <p className="mt-2 text-xs font-semibold uppercase tracking-[0.18em] text-blue">{s.hour}</p>
            <h3 className="mt-2 text-2xl font-semibold text-ink">{s.title}</h3>
            <p className="mt-2 text-gray">{s.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

/* ---------- final CTA ---------- */
const Closing = () => (
  <section className="relative overflow-hidden bg-ink px-5 py-28 text-center text-white sm:px-10 sm:py-40">
    <img src={`${A}/img/hero-rak.webp`} alt="" className="absolute inset-0 h-full w-full object-cover opacity-40" loading="lazy" />
    <div className="absolute inset-0 bg-gradient-to-b from-ink/70 via-ink/40 to-ink" />
    <div className="screen-max-width relative">
      <h2 className="g_fadeIn text-5xl font-semibold tracking-tight sm:text-7xl">Website in 24 Hours.</h2>
      <p className="g_fadeIn tabular mt-4 text-xl text-white/60">From ₹{fmt(price)}. Everything included.</p>
      <a href={BUY} className="g_fadeIn btn btn--primary mt-10 !px-10 !py-3.5 !text-base">Buy</a>
    </div>
  </section>
);

const ProductPage = () => {
  useGSAP(() => {
    animateWithGsap('.g_fadeIn', { opacity: 1, y: 0, duration: 0.9, ease: 'power2.out' });
  }, []);

  return (
    <main>
      <Hero />
      <Trusted />
      <Pages />
      <Chapter eyebrow="Content" title="Written for you." text="Send us your business details, services and photos. We write every page. No blank boxes, no waiting on copy." media={<Laptop><Shot src={SHOTS.coach} alt="Coach2Reach website" /></Laptop>} />
      <Chapter eyebrow="Logo" title="A logo, designed." text="A clean, professional mark to go with your site. Source files included, yours to keep." media={<LogoMedia />} flip alt />
      <Chapter eyebrow="Domain and hosting" title="Sorted. For a year." text="Your own .com, hosting and SSL. Set up, paid for and live. Nothing to configure." media={<DomainMedia />} />
      <Chapter eyebrow="Every phone" title="Looks right everywhere." text="Built mobile-first, so it looks sharp on the phone your customers are actually using." media={<PhonesMedia />} flip alt />
      <Compare />
      <Extras />
      <Steps />
      <Closing />
    </main>
  );
};

export default ProductPage;
